import { DetailedInsight } from '@api/api/insights';
import { KgInsight } from '@api/api/kg-insights';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Markdown } from '@terminal/components/markdown/Markdown';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePulseCitationClick } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseCitationClick';
import { MonthlySentimentState } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import {
  PulseMonthlySummary,
  PulseSentimentDirection,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import {
  getCompanyCitationId,
  linkMonthlySummaryCitations,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/linkDailySummaryCitations';
import { clsx } from 'clsx';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SentimentSummaryCard.module.scss';
import { SummaryCitationLink } from './SummaryCitationLink';

export const SentimentSummaryCard = ({
  direction,
  title,
  summary,
  themeId,
  insightsById,
  scenarioInsightIds,
  source,
  state,
  onOpen,
}: {
  direction: PulseSentimentDirection;
  title?: string;
  summary?: PulseMonthlySummary;
  themeId?: string;
  insightsById: Map<string, KgInsight | DetailedInsight>;
  scenarioInsightIds: ReadonlySet<string>;
  source: PosthogTrackingSource;
  state: MonthlySentimentState;
  onOpen: () => void;
}) => {
  const { t } = useTranslation(['widget']);
  const { handleCitationClick } = usePulseCitationClick({
    themeId,
    insightsById,
    scenarioInsightIds,
    direction,
    source,
  });
  const isInteractive = state === 'summary' || state === 'drivers';

  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!handleCitationClick(event)) {
      onOpen();
    }
  };

  const citationsById = new Map(
    (summary?.citations ?? []).map(citation => [citation.id, citation])
  );

  return (
    <div
      className={clsx(styles.card, styles[direction], {
        [styles.clickable]: isInteractive,
      })}
      onClick={isInteractive ? handleCardClick : undefined}
      data-testid={`pulse-sentiment-card-${direction}`}
    >
      <span className={styles.cardTitle}>
        {title ?? t(`widget:themeOverview.pulse.${direction}Sentiment`)}
      </span>
      <div
        className={clsx(styles.narrative, {
          [styles.emptyNarrative]: state !== 'summary',
        })}
      >
        {state === 'loading' ? (
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: '40px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        ) : state !== 'summary' ? (
          state === 'drivers' ? (
            t('widget:themeOverview.pulse.noKeyDriversInLast30DaysViewAll')
          ) : (
            t('widget:themeOverview.pulse.noDriversInLast30Days')
          )
        ) : (
          <Markdown
            message={linkMonthlySummaryCitations(
              summary?.narrative ?? '',
              summary?.citations ?? []
            )}
            components={{
              p: ({ children }) => (
                <div className={styles.paragraph}>{children}</div>
              ),
              a: ({ children, href }) => {
                const citationId = getCompanyCitationId(href ?? null);

                return (
                  <SummaryCitationLink
                    href={href}
                    entityTag={
                      citationId !== null
                        ? citationsById.get(citationId)?.entity_tag
                        : undefined
                    }
                  >
                    {children}
                  </SummaryCitationLink>
                );
              },
            }}
          />
        )}
      </div>
    </div>
  );
};
